import PropTypes from 'prop-types';
import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import Stack from '@mui/material/Stack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CopyAllRoundedIcon from '@mui/icons-material/CopyAllRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useTheme } from '@mui/material/styles';

import QPopper from 'components/QPopper';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

const useStyles = makeStyles()((theme) => ({
  dumpButton: {
    position: 'absolute', // 'fixed' doesn't work with scroll
    zIndex: theme.zIndex.mobileStepper,
    // color: 'black',
    // backgroundColor: 'white',
  },
  content: {
    maxWidth: '60vw',
    maxHeight: '60vh',
    overflow: 'scroll',
  },
}));

const stopProp = (e) => e?.stopPropagation();

const Dump = (props) => {
  const { id, children, obj, value, style, ...otherProps } = props;
  const { classes } = useStyles();
  const theme = useTheme();
  const buttonRef = useRef();
  const contentRef = useRef();
  const [show, setShow] = useState();
  const [showDetails, setShowDetails] = useState();
  const [pinWindow, setPinWindow] = useState();
  const [dragDataX, setDragDataX] = useState(0);
  const [dragDataY, setDragDataY] = useState(0);
  const offset = useMemo(() => [dragDataX, dragDataY], [dragDataY, dragDataX]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Alt') setShow(true);
  }, []);
  const handleKeyUp = useCallback((e) => {
    if (e.key === 'Alt') setShow(false);
  }, []);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const val = obj ?? value;
  const data = useMemo(() => (showDetails && typeof val === 'function' ? val() : val), [val, showDetails]);

  const dragRef = React.useRef({});

  return (
    <>
      {(show || showDetails) && (
        <Button
          startIcon={<BuildCircleRoundedIcon fontSize='small' />}
          variant='outlined'
          className={classes.dumpButton}
          {...otherProps}
          onClick={(event) => {
            buttonRef.current = event.currentTarget;
            setShowDetails(true);
            setDragDataX(0);
            setDragDataY(0);
            event.stopPropagation();
          }}
          size='small'
          component='div' // div to prevent 'nested button' html complaint
          role='button'
          sx={{ border: `1px dashed ${theme.palette.divider}`, padding: 1, borderRadius: '8px' }}
        >
          <Typography variant='captionMedium' noWrap fontFamily='monospace'>
            {id ?? data?.id}
          </Typography>

          {(id ?? data?.id) && (
            <IconButton
              id='copy-id-button'
              onClick={(event) => {
                navigator.clipboard.writeText(id ?? data?.id);
                event.stopPropagation();
                dispatchSimpleNotification('ID Copied to Clipboard');
              }}
              size='small'
            >
              <CopyAllRoundedIcon fontSize='inherit' />
            </IconButton>
          )}
        </Button>
      )}

      {(showDetails || pinWindow) && (
        <Draggable
          nodeRef={dragRef}
          handle='#header'
          onDrag={(event, uiData) => {
            const newDragDataX = dragDataX + (uiData.deltaX || 0);
            setDragDataX(newDragDataX);
            const newDragDataY = dragDataY + (uiData.deltaY || 0);
            setDragDataY(newDragDataY);
            if (!pinWindow) {
              setPinWindow(true);
            }
          }}
        >
          <QPopper
            ref={(ref) => (dragRef.current = ref ?? {})}
            anchorEl={buttonRef.current}
            open={showDetails}
            placement='bottom-start'
            onClickAway={() => !pinWindow && setShowDetails(false)}
            modifiers={[
              {
                name: 'offset',
                options: { offset },
              },
              {
                name: 'flip',
                enabled: false,
              },
              {
                name: 'preventOverflow',
                enabled: false,
              },
            ]}
            onClick={stopProp}
          >
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              id='header'
              backgroundColor='background.paper'
              sx={{ cursor: 'move' }}
            >
              <Stack direction='row' alignItems='center' gap={1}>
                <DragIndicatorRoundedIcon />
                <Button
                  id='copy-content-button'
                  variant='outlined'
                  size='small'
                  endIcon={<CopyAllRoundedIcon />}
                  onClick={(event) => {
                    navigator.clipboard.writeText(contentRef.current.innerText);
                    event.stopPropagation();
                    dispatchSimpleNotification('Content Copied to Clipboard');
                  }}
                >
                  Copy to clipboard
                </Button>
              </Stack>
              <Stack direction='row' alignItems='center' gap={1}>
                <IconButton
                  variant='tonal'
                  onClick={(event) => {
                    setPinWindow(!pinWindow);
                    event.stopPropagation();
                  }}
                  size='small'
                >
                  {pinWindow ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
                </IconButton>

                <IconButton
                  size='small'
                  variant='tonal'
                  onClick={(event) => {
                    setShowDetails(false);
                    event.stopPropagation();
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>
            </Box>

            <div ref={contentRef} className={classes.content}>
              {children || null}
              {data && (
                <pre>
                  <Typography variant='paragraphRegular' fontFamily='monospace'>
                    {JSON.stringify(data, null, 2)}
                  </Typography>
                </pre>
              )}
            </div>
          </QPopper>
        </Draggable>
      )}
    </>
  );
};

Dump.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  obj: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]),
  id: PropTypes.any,
  style: PropTypes.object,
};

function DumpWrapper(props) {
  const debug = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'debug'));
  if (!debug) {
    return null;
  }
  return <Dump {...props} />;
}

DumpWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  obj: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func]),
  id: PropTypes.any,
  style: PropTypes.object,
};

export default DumpWrapper;
